<script setup lang="ts">
/**
 * Карточка проекта в хабе — макет 17:819: 354×148, отступы 20, точка цвета,
 * имя, ключ моноширинным, замок у приватного, описание в две строки и подвал
 * со счётчиками и аватарами.
 *
 * Высота фиксированная: карточки стоят сеткой, и подпрыгивающий ряд из-за
 * описания в одну строку выглядел бы поломкой вёрстки.
 */
import { AppAvatarGroup } from '@vinprop/design-system'
import { Lock } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Project, ProjectMember } from '../api'

const props = withDefaults(
  defineProps<{
    project: Project
    members?: ProjectMember[]
  }>(),
  { members: () => [] },
)

const { t } = useI18n()

/** Сколько аватаров показываем; остальные уходят в «+N». */
const AVATARS_SHOWN = 3

// Счётчик рисуем сами: в макете это серый текст рядом со стопкой, а не ещё
// один кружок в ней, как умеет сам компонент.
const avatars = computed(() =>
  props.members.slice(0, AVATARS_SHOWN).map((member) => ({ name: member.name })),
)

const hiddenCount = computed(() => Math.max(0, props.members.length - AVATARS_SHOWN))

const dotColor = computed(() => props.project.color || 'var(--color-primary)')
</script>

<template>
  <RouterLink class="project-card" :to="`/p/${project.key}/board`">
    <div class="project-card__head">
      <span class="project-card__dot" :style="{ background: dotColor }" />
      <span class="project-card__name">{{ project.name }}</span>
      <span class="project-card__key">{{ project.key }}</span>
      <Lock
        v-if="project.isPrivate"
        class="project-card__lock"
        :size="12"
        :aria-label="t('projects.card.private')"
      />
    </div>

    <p class="project-card__description">{{ project.description }}</p>

    <div class="project-card__foot">
      <span class="project-card__counts">
        {{ t('projects.card.open', project.openIssueCount) }}
      </span>

      <div v-if="members.length" class="project-card__members">
        <AppAvatarGroup :users="avatars" :size="24" :max="AVATARS_SHOWN" />
        <span v-if="hiddenCount" class="project-card__more">+{{ hiddenCount }}</span>
      </div>
    </div>
  </RouterLink>
</template>

<style scoped>
.project-card {
  display: flex;
  flex-direction: column;
  height: 148px;
  padding: 18px 20px 20px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  text-decoration: none;
}

.project-card:hover {
  border-color: var(--color-primary);
}

.project-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 19px;
}

.project-card__dot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
}

.project-card__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-semibold);
  line-height: 19px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-card__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  line-height: 16px;
}

.project-card__lock {
  color: var(--color-text-muted);
  flex-shrink: 0;
}

/* Ровно две строки: третья ушла бы под подвал, а обрезанная по высоте строка
   читается как случайный обрыв. */
.project-card__description {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  margin: 12px 0 0;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  line-height: 18px;
}

.project-card__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  height: 24px;
}

.project-card__counts {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

.project-card__members {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.project-card__more {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
